import { Fragment } from 'react';
import { useIsMobile } from '../contexts/ResponsiveContext';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import BackButton from './BackButton';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { formatLessonDate } from '../utils/lessonDate';

interface BundleCompleteProps {
  order: Order;
  lessons: Lesson[];  // All delivered lessons, sorted by lessonNumber
  onLessonClick: (lesson: Lesson) => void;
  onBack?: () => void;
  onViewReport?: () => void;
  /** Called when the player taps "Start New Program" — navigates to the OR screen */
  onStartNewProgram?: () => void;
  /** Disable "Start New Program" when the player already has an active training program with this coach */
  hasActiveProgram?: boolean;
}

export default function BundleComplete({ order, lessons, onLessonClick, onBack, onViewReport, onStartNewProgram, hasActiveProgram }: BundleCompleteProps) {
  const isMobile = useIsMobile();

  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachDisplayName = liveCoach?.name ?? order.coachName ?? '';
  const coachAvatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;

  const totalLessons = order.totalLessons ?? lessons.length;
  const coachFirstName = coachDisplayName.split(' ')[0] || 'your coach';
  const coachInitials = (coachDisplayName || 'C')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const sortedLessons = [...lessons].sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0));

  const startDate = order.createdAt
    ? new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const lastDelivered = sortedLessons[sortedLessons.length - 1]?.deliveredAt;
  const endDate = lastDelivered
    ? new Date(lastDelivered).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : startDate;

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-db-bg">
      <div className="p-6 md:p-8 pt-5 md:pt-8 max-w-[760px] w-full mx-auto">

        <BackButton onClick={onBack} fallback="/lessons" size="sm" sticky />

        {/* Complete banner */}
        <div className={`bg-white rounded-2xl px-6 py-5 mb-4 ${isMobile ? '' : 'flex items-center gap-4'} border-[1.5px] border-biscayne`}>
          <div className={isMobile ? 'mb-3' : 'flex-1'}>
            <div className="text-[14px] font-extrabold text-db-text-1 mb-1">
              {isMobile ? 'Program complete · report ready!' : 'Training program complete. Progress report ready!'}
            </div>
            <div className="text-[12px] text-db-text-2 leading-[1.5]">
              {coachFirstName} wrote a summary of your {totalLessons}-lesson arc — what improved, what to focus on, and what's next.
            </div>
          </div>
          <button
            className={`text-[12px] font-semibold text-biscayne border border-[rgba(0,0,0,0.15)] rounded-lg px-3 py-1.5 whitespace-nowrap hover:bg-db-bg transition-colors ${isMobile ? 'w-full text-center' : ''}`}
            onClick={onViewReport}
          >
            View Report →
          </button>
        </div>

        {/* Training Program card — all complete */}
        <div className="bg-white rounded-2xl px-6 py-6 mb-4 shadow-sm border-[1.5px] border-biscayne">
          {/* Top row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 aspect-[4/5] rounded-[8px] bg-biscayne flex items-center justify-center overflow-hidden flex-shrink-0">
                {coachAvatarUrl ? (
                  <img src={coachAvatarUrl} alt={coachDisplayName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[15px] font-bold text-white">{coachInitials}</span>
                )}
              </div>
              <div>
                <div className="text-[14px] font-extrabold text-db-text-1">{coachDisplayName}</div>
                <div className="text-[12px] text-db-text-2">
                  Training program{dateRange ? ` · ${dateRange}` : ''}
                </div>
              </div>
            </div>
            <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-green-50 text-green-700 whitespace-nowrap">
              Complete
            </span>
          </div>

          {/* All-complete progress tracker */}
          <div className="bg-db-bg rounded-xl px-4 py-4">
            {isMobile ? (
              <div className="text-[14px] font-bold text-db-text-1 mb-3">
                Training program · {totalLessons} of {totalLessons} · Complete ✓
              </div>
            ) : (
              <div className="flex items-center justify-between mb-3">
                <div className="text-[13px] font-bold text-db-text-1">Training program progress</div>
                <div className="text-[13px] font-extrabold text-db-text-1">{totalLessons} of {totalLessons} · Complete ✓</div>
              </div>
            )}

            {/* Step indicators — all done */}
            <div className="flex items-center gap-0 mb-3">
              {Array.from({ length: totalLessons }).map((_, i) => (
                <Fragment key={i}>
                  <div className={`flex-1 flex flex-col items-center ${isMobile ? 'gap-0' : 'gap-1.5'}`}>
                    <div className="w-7 h-7 rounded-full bg-biscayne flex items-center justify-center">
                      <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    {!isMobile && (
                      <>
                        <div className="text-[10px] font-semibold text-db-text-2">Lesson {i + 1}</div>
                        <div className="text-[10px] text-db-text-3">Delivered</div>
                      </>
                    )}
                  </div>
                  {i < totalLessons - 1 && (
                    <div className={`flex-1 h-[2px] ${isMobile ? 'mb-0' : 'mb-6'} bg-biscayne`} />
                  )}
                </Fragment>
              ))}
            </div>

            {/* Progress report CTA inside tracker */}
            <div className={`bg-[#EBF2FF] rounded-[9px] px-3.5 py-3 ${isMobile ? '' : 'flex items-center justify-between'} border border-[#BFDBFE]`}>
              <div className={isMobile ? 'mb-2.5' : ''}>
                <div className="text-[12px] font-bold text-db-text-1">All {totalLessons} lessons complete</div>
                <div className="text-[11px] text-db-text-2">Your progress report is ready</div>
              </div>
              <button
                className={`bg-biscayne text-white text-[12px] font-bold px-3 py-1.5 rounded-lg whitespace-nowrap hover:bg-db-navy-hover transition-colors ${isMobile ? 'w-full text-center' : ''}`}
                onClick={onViewReport}
              >
                View Report →
              </button>
            </div>
          </div>
        </div>

        {/* All delivered lessons */}
        <div className="bg-white rounded-2xl px-6 py-5 mb-4 shadow-sm">
          <div className="text-[14px] font-extrabold text-db-text-1 mb-3.5">Delivered Lessons</div>
          <div className="flex flex-col divide-y divide-db-border">
            {sortedLessons.map((dl) => (
              <div
                key={dl.id}
                className="flex items-center gap-3 py-3 cursor-pointer rounded-lg px-1 hover:bg-db-bg transition-colors"
                onClick={() => onLessonClick(dl)}
              >
                <div className="w-9 h-9 rounded-full bg-biscayne flex items-center justify-center text-[12px] font-extrabold text-white flex-shrink-0">
                  {dl.lessonNumber ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-db-text-1">Lesson {dl.lessonNumber}</div>
                  <div className="text-[11.5px] text-db-text-2">
                    {dl.deliveredAt
                      ? `Delivered ${formatLessonDate(dl.deliveredAt)} · `
                      : ''}
                    {coachDisplayName}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onLessonClick(dl); }}
                  className="text-[11px] font-semibold text-biscayne border border-[rgba(0,0,0,0.15)] rounded-lg px-2.5 py-1 hover:bg-db-bg transition-colors flex-shrink-0"
                >
                  Watch →
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Start New Program CTA */}
        <div className="flex gap-3 pb-6">
          <button
            onClick={onStartNewProgram}
            disabled={hasActiveProgram}
            title={hasActiveProgram ? 'You already have an active training program with this coach' : undefined}
            className="flex-1 py-3 bg-biscayne rounded-xl text-[14px] font-bold text-white hover:bg-db-navy-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-center"
          >
            Start New Program
          </button>
        </div>

      </div>
    </div>
  );
}
